import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import camelcaseKeys from "camelcase-keys";
import { format } from "date-fns/format";
import { parseISO } from "date-fns/parseISO";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import React, { createElement, Dispatch, SetStateAction } from "react";

import { ProgressBar, StepType } from "../components";
import { ModalState, ReportType, Session } from "../types";
import { possibleMovementTypes } from "./Constants";

// Join together the strings in the class name
export const joinClassNames = (...classes: any[]): string => {
  return classes.filter(Boolean).join(" ");
};

// Return the time of day - morning, afternoon, or evening.
export function timeOfDay(): string {
  const currentHour: number = new Date().getHours();
  if (currentHour > 3 && currentHour < 12) {
    return "morning";
  } else if (currentHour > 12 && currentHour < 6) {
    return "afternoon";
  } else {
    return "evening";
  }
}

// Taken from https://stackoverflow.com/a/46774740/9406847
export const toTitleCase = (str: string): string => {
  const articles = ["a", "an", "the"];
  const conjunctions = ["for", "and", "nor", "but", "or", "yet", "so"];
  const prepositions = [
    "with",
    "at",
    "from",
    "into",
    "upon",
    "of",
    "to",
    "in",
    "for",
    "on",
    "by",
    "like",
    "over",
    "plus",
    "but",
    "up",
    "down",
    "off",
    "near",
  ];

  // The list of spacial characters can be tweaked here
  const replaceCharsWithSpace = (replaceStr: string) =>
    replaceStr.replace(/[^0-9\p{L}\-\.'&/\\]/u, " ").replace(/(\s\s+)/gi, " ");
  const capitalizeFirstLetter = (capitalizeStr: string) =>
    capitalizeStr.charAt(0).toUpperCase() + capitalizeStr.substr(1);
  const normalizeStr = (normalStr: string) => normalStr.toLowerCase().trim();
  const capitalizeHyphenated = (hyphenatedStr: string) =>
    hyphenatedStr
      .split("-")
      .map((word) => capitalizeFirstLetter(word))
      .join("-");
  const shouldCapitalize = (
    word: string,
    fullWordList: string[],
    posWithinStr: number,
  ) => {
    if (posWithinStr == 0 || posWithinStr == fullWordList.length - 1) {
      return true;
    }

    return !(
      articles.includes(word) ||
      conjunctions.includes(word) ||
      prepositions.includes(word)
    );
  };

  str = replaceCharsWithSpace(str);
  str = normalizeStr(str);
  str = capitalizeHyphenated(str);

  let words = str.split(" ");
  if (words.length <= 2) {
    // Strings less than 3 words long should always have first words capitalized
    words = words.map((w) => capitalizeFirstLetter(w));
  } else {
    for (let i = 0; i < words.length; i++) {
      words[i] = shouldCapitalize(words[i], words, i)
        ? capitalizeFirstLetter(words[i])
        : words[i];
    }
  }

  return words.join(" ");
};

// Special function for proper names that preserves original capitalization
// This is particularly useful for names with apostrophes like O'Dette, D'Angelo, etc.
export const toProperNameCase = (str: string): string => {
  if (!str) return str;

  // Words that should remain lowercase in proper names (prepositions, articles, etc.)
  const lowercaseWords = [
    "van",
    "der",
    "den",
    "von",
    "de",
    "la",
    "le",
    "du",
    "del",
    "della",
    "delle",
    "di",
    "da",
    "dal",
    "dalla",
    "dalle",
    "al",
    "alla",
    "alle",
    "el",
    "ella",
    "elle",
    "il",
    "lo",
    "los",
    "las",
    "un",
    "una",
    "uno",
    "unas",
    "unos",
  ];

  // Split by spaces to handle multi-word names
  const words = str.split(" ");

  return words
    .map((word, index) => {
      if (!word) return word;

      // Handle hyphenated names (e.g., McDonald-Smith)
      if (word.includes("-")) {
        return word
          .split("-")
          .map((part) => {
            if (!part) return part;
            // Ensure first letter is capitalized for each part
            return part.charAt(0).toUpperCase() + part.slice(1);
          })
          .join("-");
      }

      // For multi-word names, handle special cases
      if (words.length > 1) {
        const lowerWord = word.toLowerCase();

        // If it's the first word, always capitalize
        if (index === 0) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        }

        // If it's a lowercase word in our list, keep it lowercase
        if (lowercaseWords.includes(lowerWord)) {
          return word.toLowerCase();
        }

        // Otherwise, capitalize the first letter
        return word.charAt(0).toUpperCase() + word.slice(1);
      }

      // For single words, always capitalize the first letter
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

// Used https://redux-toolkit.js.org/rtk-query/usage/automated-refetching for a guide for this method
// Helper method to return a list of results with the abstract LIST tag type and individual tags for each element
export function providesList<
  R extends { id: string | number }[],
  T extends string,
>(resultsWithIds: R | undefined, tagType: T): any[] {
  return resultsWithIds
    ? [
        { type: tagType, id: "LIST" },
        ...resultsWithIds.map(({ id }) => ({ type: tagType, id })),
      ]
    : [{ type: tagType, id: "LIST" }];
}

// Upserts a player into a locally-stored list by their stable internal `id`.
// Identity must key on `id` (the immutable DB UUID), never on `orgPlayerId`,
// which orgs can reassign at will. Keying on the mutable orgPlayerId leaves a
// stale duplicate row after a rename, which then sends edits to a no-longer-
// existing org_player_id and 404s.
export function upsertPlayerById<T extends { id: string }>(
  list: T[],
  player: T,
): T[] {
  return list.filter((existing) => existing.id !== player.id).concat(player);
}

// As we need this to return many types that are different, we're using any as the return type
// This function will handle arrays of results as well, including nested objects using a recursive approach
export function camelCaseResponse(response: Record<string, unknown>): any {
  return camelcaseKeys(response, { deep: true });
}

export const enableNextButtonInModal = (
  modalState: ModalState,
  setModalState: Dispatch<SetStateAction<ModalState>>,
  stepList: StepType[],
) => {
  if (modalState.steps.length) {
    setModalState((prev) => ({
      ...prev,
      steps: stepList,
    }));
  }
};

const buildStatusContainer = (
  session: Session,
  player: any = undefined,
  onErrorClick?: () => void,
) => {
  let statusColor: string;
  let status: string;
  if (player) {
    statusColor =
      player.percentComplete === 100 && player.failedMovements === 0
        ? "text-green-600"
        : "text-red-500";
    status =
      player.percentComplete === 100 && player.failedMovements === 0
        ? "Completed"
        : player.inProgressMovements === 0 && player.failedMovements > 0
          ? `${player.failedMovements} issues`
          : player.percentComplete === 0
            ? "Awaiting Processing"
            : "Processing";
  } else {
    statusColor =
      session.status === "completed" || session.status === "processed"
        ? "text-green-600"
        : session.status === "in-progress"
          ? "text-[#F59E0B]"
          : "text-red-500";
    status =
      session.failedMovements > 0
        ? `${session.failedMovements} issues`
        : session.status === "completed" || session.status === "processed"
          ? "Completed"
          : toTitleCase(session.status);
  }

  const isFailed =
    (player && player.failedMovements > 0) ||
    (!player && (session.status === "failed" || session.failedMovements > 0));

  const statusChildren = isFailed
    ? [
        createElement(ExclamationTriangleIcon, {
          key: "icon",
          className: "inline-block w-4 h-4 mr-1 stroke-2",
        }),
        status,
      ]
    : status;

  const elementType = isFailed && onErrorClick ? "button" : "div";
  const elementProps: any = {
    className: `${statusColor} status-element ${
      isFailed && onErrorClick
        ? "inline-flex items-center cursor-pointer hover:underline"
        : "w-40 block truncate hover:text-clip hover:whitespace-normal hover:break-all"
    }`,
  };

  if (isFailed && onErrorClick) {
    elementProps.onClick = onErrorClick;
    elementProps.type = "button";
  }

  const customStatusElement = createElement(
    elementType,
    elementProps,
    statusChildren,
  );
  if (status === "In Progress") {
    return createElement(
      "div",
      { className: "flex flex-col pr-1 w-3/4" },
      <ProgressBar progress={session.percentComplete} />,
    );
  } else {
    return createElement(
      "div",
      {
        className: "flex flex-col pr-1 w-3/4",
      },
      customStatusElement,
    );
  }
};

export function parseCompletedSessions(
  data: Session[],
  playerReports: any[] = [],
  onErrorClick?: (session: Session) => void,
): Session[] {
  const copiedData = [...data];
  copiedData.forEach((session, index) => {
    const typeAndVenue = `${session.sessionType.name}, ${session.sessionVenue}`;
    copiedData[index] = {
      ...session,
      statusComponent: buildStatusContainer(
        session,
        undefined,
        onErrorClick ? () => onErrorClick(session) : undefined,
      ),
      typeAndVenue: typeAndVenue,
      reports: playerReports
        ? playerReports.filter((report: any) => {
            if (report.sessionId == session.id) {
              return report;
            }
          })
        : [],
      defaultSortDate: format(new Date(session.sessionDate), "yyyy-MM-dd"),
      hand: determineHandsForReports(
        playerReports
          ? playerReports.filter((report: any) => {
              if (report.sessionId == session.id) {
                return report;
              }
            })
          : [],
      ),
    };
  });
  copiedData.sort((a: Session, b: Session): number => {
    return a.defaultSortDate > b.defaultSortDate ? -1 : 1;
  });
  return copiedData;
}

export function parseInProgressSessions(
  data: Session[],
  playerReports: any[] = [],
): Session[] {
  const copiedData = [...data];
  copiedData.forEach((session: Session, index) => {
    const statusElement = <ProgressBar progress={session.percentComplete} />;
    const typeAndVenue = `${session.sessionType.name}, ${session.sessionVenue}`;
    copiedData[index] = {
      ...session,
      statusComponent: statusElement,
      typeAndVenue: typeAndVenue,
      reports: playerReports
        ? playerReports.filter((report: any) => {
            if (report.sessionId == session.id) {
              return report;
            }
          })
        : [],
      hand: determineHandsForReports(
        playerReports
          ? playerReports.filter((report: any) => {
              if (report.sessionId == session.id) {
                return report;
              }
            })
          : [],
      ),
    };
  });
  return copiedData.sort(function (a, b) {
    if (a.percentComplete > b.percentComplete) {
      return -1;
    } else {
      return 1;
    }
  });
}

export const parseDataForSessionDetails = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  sessionPlayers: any,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  selectedSession: any,
  sessionReports: any[] = [],
  onErrorClick?: (
    session: any,
    orgPlayerId?: string,
    failedMovementsCount?: number,
  ) => void,
): any[] => {
  const copiedPlayers = [...sessionPlayers];
  copiedPlayers.forEach((player: any, index: number) => {
    const movementCount = processTotalMovements(
      player.totalMovements,
      selectedSession.sessionType.slug,
    );

    const playerReports = sessionReports.filter((report: any) => {
      if (report.primaryPlayers[0].id == player.id) {
        return report;
      }
    });
    copiedPlayers[index] = {
      ...player,
      playerName: `${player.firstName} ${player.lastName}`,
      numberOfMovements: movementCount,
      statusComponent: buildStatusContainer(
        selectedSession,
        player,
        onErrorClick
          ? () =>
              onErrorClick(
                selectedSession,
                player.orgPlayerId,
                player.failedMovements,
              )
          : undefined,
      ),
      createdAt: selectedSession.createdAt,
      reports: playerReports,
      tableValues: {
        numberOfMovements: processNumMovements(
          movementCount,
          selectedSession.sessionType.slug,
        ),
      },
      hand: determineHandsForReports(playerReports),
    };
  });
  return copiedPlayers;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const determineHandsForReports = (reports: any): string => {
  const hands = reports
    .map((report: ReportType): string => {
      if (report.domHand === "RHA") {
        return "Right";
      } else if (report.domHand === "LHA") {
        return "Left";
      } else if (report.domHand === "SHA") {
        return "Switch";
      } else {
        return "";
      }
    })
    .filter((hand: string): hand is string => Boolean(hand));

  const uniqueHands: string[] = Array.from(new Set(hands));

  return uniqueHands.length > 1
    ? uniqueHands.join(", ")
    : (uniqueHands[0] ?? "");
};

export const processTotalMovements = (
  totalMovements: {
    [key: string]: number;
  },
  sessionType: string,
): { [key: string]: number } => {
  if (sessionType === "movement-screen") {
    const totalMovementCount = Object.values(totalMovements).reduce(
      (sum, count) => sum + (count || 0),
      0,
    );
    return { movementsInScreen: totalMovementCount, ...totalMovements };
  } else {
    const sessionMovements: { [key: string]: number } = {};
    for (const [key, value] of Object.entries(totalMovements)) {
      sessionMovements[key] = value ?? undefined;
    }
    return sessionMovements;
  }
};

export const processNumMovements = (
  numMovements: {
    [key: string]: number;
  },
  sessionTypeSlug: string,
  modal?: boolean,
): string => {
  if (sessionTypeSlug === "movement-screen" && modal) {
    return `${numMovements.movementsInScreen} Movements`;
  } else {
    const movements = [];
    for (const [movement, count] of Object.entries(numMovements)) {
      if (modal) {
        movements.push(
          `${count} ${possibleMovementTypes[movement] || "Movements"}`,
        );
      } else {
        movements.push(
          `${possibleMovementTypes[movement] || "Movements"}: ${count}`,
        );
      }
    }
    return movements.join(", ");
  }
};

export const disableAddPlayer = (orgType: string): boolean => {
  if (orgType === "NBA") {
    return true;
  } else {
    return false;
  }
};

export const disableEditPlayer = (orgType: string): boolean => {
  if (orgType === "NBA") {
    return true;
  } else {
    return false;
  }
};

export const showPlayerHandedness = (orgType: string): boolean => {
  if (orgType === "NBA") {
    return false;
  } else {
    return true;
  }
};

export const disableCustomAnalysis = (orgType: string): boolean => {
  if (orgType === "NBA") {
    return true;
  } else {
    return false;
  }
};

export const formatMovementType = (movementType: string): string => {
  const type = movementType
    .replace(/-/g, " ")
    .replace(/baseball\s*|basketball\s*/gi, "");
  return toTitleCase(type);
};

export async function downloadS3Files(
  urls: string[],
  toZip: boolean = false,
  zipFileName: string = `reboot_motion_archive_${Date.now()}.zip`,
) {
  try {
    const files = await fetchFiles(urls);
    if (toZip) {
      const zip = new JSZip();
      files.forEach(({ fileName, blob }) => {
        zip.file(fileName, blob);
      });
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, zipFileName);
    } else {
      files.forEach(({ fileName, blob }) => {
        saveAs(blob, fileName);
      });
    }
  } catch (error) {
    console.error("Error downloading files:", error);
  }
}

async function fetchFiles(
  urls: string[],
): Promise<{ fileName: string; blob: Blob }[]> {
  const fetchFile = async (url: string) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}`);
    }
    return response.blob();
  };

  return Promise.all(
    urls.map(async (url) => {
      const blob = await fetchFile(url);
      const fileName = decodeURI(getFileNameFromPresignedUrl(url));
      return { fileName, blob };
    }),
  );
}

function getFileNameFromPresignedUrl(url: string): string {
  const parsedUrl = new URL(url);
  const pathname = parsedUrl.pathname;
  return pathname.substring(pathname.lastIndexOf("/") + 1);
}

export const convertUTCToLocalTime = (date: string): string => {
  try {
    const utcDate = parseISO(date + "Z");
    const localDate = new Date(utcDate.toLocaleString());
    return format(localDate, "M/d/yyyy");
  } catch {
    return "Invalid date";
  }
};
